// /**
//  * Products - list
//  */

// React
import React from 'react'

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles
} from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Paper
} from '@material-ui/core'

import RonadIcons from '../../components/iconComponent'

// Type
type Dimensions = {
  count: number
  length: number
  width: number
  height: number
  weight: number
  delete: number
}

type Data = Dimensions
type Props = {
  data: Dimensions[]
  modal?: boolean
  setDimensions: (data: Dimensions[]) => void
}

type Ord = 'asc' | 'desc'
interface HeadCell {
  id: keyof Data
  label: string
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void
  order: Ord
  orderBy: string
  rowCount: number
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: '#d3d6e8 !important',
      color: '#fffff'
    },
    body: {
      fontSize: 14
    }
  })
)(TableCell)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-root': {
        padding: '15px'
      },
      '& .MuiIconButton-sizeSmall': {
        padding: '8px'
      }
    },
    paper: {
      width: '100%'
    },
    columnText: {
      padding: '0 10px'
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%'
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1
    }
  })
)

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const headCells: HeadCell[] = [
  {
    id: 'count',
    label: 'تعداد'
  },
  {
    id: 'length',
    label: 'طول'
  },
  {
    id: 'width',
    label: 'عرض'
  },
  {
    id: 'height',
    label: 'ارتفاع'
  },
  {
    id: 'weight',
    label: 'وزن'
  },
  {
    id: 'delete',
    label: 'حذف'
  }
]

const IconSort = (order: Ord) => {
  return (
    <RonadIcons name={order === 'asc' ? 'chevronDown' : 'chevronUp'} size={1} />
  )
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props
  const classes = useStyles()
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => {
          const orderStatus = orderBy === headCell.id ? order : 'asc'
          return (
            <StyledTableCell
              key={headCell.id}
              align='center'
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          )
        })}
      </TableRow>
    </TableHead>
  )
}

export default function DimensionsListInPage(props: Props) {
  const [order, setOrder] = React.useState<Ord>('desc')
  const [orderBy, setOrderBy] = React.useState<keyof Data>('count')

  const [dense] = React.useState(true)

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        '& .MuiTableCell-root': {
          padding: '15px'
        },
        '& .MuiIconButton-sizeSmall': {
          padding: '8px'
        }
      },
      table: {
        minWidth: props.modal ? 500 : 750
      }
    })
  )

  const classes = useStyles()

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const deleteDimension = (id: any) => {
    const updatedDimensions = props.data.filter((row: any) => row.id !== id)
    props.setDimensions(updatedDimensions)
  }

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby='tableTitle'
          size={dense ? 'small' : 'medium'}
          aria-label='enhanced table'
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`
                return (
                  <TableRow key={index}>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      {row.count}
                    </TableCell>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      {row.length}
                    </TableCell>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      {row.width}
                    </TableCell>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      {row.height}
                    </TableCell>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      {row.weight}
                    </TableCell>
                    <TableCell
                      component='th'
                      id={labelId}
                      scope='row'
                      align='center'
                    >
                      <IconButton
                        aria-label='cancele'
                        title='حذف آیتم '
                        size='small'
                        onClick={() => deleteDimension(row.id)}
                        data-cy='removeBtn'
                      >
                        <RonadIcons
                          name='times'
                          className='pointer text-danger'
                          // size={0.8}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
